// How you lost ground: why a middlegame that went wrong went wrong, from the
// whole middlegame rather than one move.
//
// Every counted blunder or miss of the reader's in the middlegame costs win
// chance, in percentage points of expected score: a blunder the chance before
// the move less the chance after it; a miss the chance of the engine's best
// line less the chance of the move played. Both are the score before the move
// (the best line's value) against the score after it, so one formula serves.
// Each move takes the first cause that fits, checked in this order:
//
//   time        under a minute on the reader's clock after the move
//   missed      a miss: a winning move not played
//   tactic      allowed mate, a piece left hanging, a fork walked into, another tactic
//   positional  none of those: a quiet move that cost a lot
//
// The game's cause is the one whose moves cost the most in total. A tie goes
// to the cause of the move that crossed a winning / level / lost line
// downwards (the costliest such move, then the earlier), and failing that to
// the cause of the earlier move. The move to practise is the costliest move of
// the game's cause, the earlier on a tie. Only the reader's moves count; a
// game without a counted mistake has no cause. Pure: the caller (api-insights'
// fact extractor) supplies the moves.
import type { Color, GroundCause, MoveKind } from '@chess/api-model';
import { winChance, type Pattern } from './classify.ts';

/** Under this many seconds on the clock after the move, the move's cause is the clock. */
export const GROUND_TIME_SECONDS = 60;

/** One of the reader's counted mistakes in the middlegame. */
export interface GroundMove {
  /** 1-based; odd plies are White's. */
  ply: number;
  san: string;
  kind: MoveKind;
  /** The reader's score in pawns before the move: for a miss, the best line's. */
  before: number;
  /** The reader's score in pawns after the move played. */
  after: number;
  /** The classifier's pattern for the move (`patternOf`). */
  pattern: Pattern;
  /** Seconds left on the reader's clock after the move; undefined when the PGN has no clocks. */
  clockSeconds?: number;
}

export interface GroundCauseResult {
  cause: GroundCause;
  /** The costliest move of that cause: the one to practise. */
  practice: { ply: number; san: string };
  /** Win chance lost to each cause over the middlegame, in percentage points. */
  costs: Record<GroundCause, number>;
}

const TACTICAL: ReadonlySet<Pattern> = new Set<Pattern>(['mate', 'hanging', 'fork', 'tactic']);
const EPSILON = 1e-9;

/** The cause of one move, checked in order: time, missed, tactic, positional. */
export function moveCause(m: Pick<GroundMove, 'kind' | 'pattern' | 'clockSeconds'>): GroundCause {
  if (m.clockSeconds !== undefined && m.clockSeconds < GROUND_TIME_SECONDS) return 'time';
  if (m.kind === 'miss') return 'missed';
  return TACTICAL.has(m.pattern) ? 'tactic' : 'positional';
}

/** What the move cost the reader, in percentage points of win chance; never negative. */
export function moveCost(m: Pick<GroundMove, 'before' | 'after'>): number {
  return Math.max(0, (winChance(m.before) - winChance(m.after)) * 100);
}

/** 2 winning, 1 level, 0 lost, from the Winning setting. */
const rank = (pawns: number, winningPawns: number) => (pawns >= winningPawns ? 2 : pawns <= -winningPawns ? 0 : 1);

const isReaders = (ply: number, reader: Color) => (ply % 2 === 1 ? 'white' : 'black') === reader;

/** Why the reader's middlegame went wrong; undefined when none of the moves is theirs. */
export function lostGroundCause(moves: readonly GroundMove[], reader: Color, winningPawns: number): GroundCauseResult | undefined {
  const own = moves.filter((m) => isReaders(m.ply, reader)).sort((a, b) => a.ply - b.ply);
  if (!own.length) return undefined;
  const scored = own.map((m) => ({ m, cause: moveCause(m), cost: moveCost(m), crossed: rank(m.after, winningPawns) < rank(m.before, winningPawns) }));
  const costs: Record<GroundCause, number> = { tactic: 0, missed: 0, time: 0, positional: 0 };
  for (const s of scored) costs[s.cause] += s.cost;
  const top = Math.max(...scored.map((s) => costs[s.cause]));
  const tied = new Set(scored.filter((s) => costs[s.cause] >= top - EPSILON).map((s) => s.cause));
  let cause: GroundCause;
  if (tied.size === 1) cause = [...tied][0];
  else {
    const crossing = scored.filter((s) => s.crossed && tied.has(s.cause)).sort((a, b) => b.cost - a.cost || a.m.ply - b.m.ply)[0];
    cause = (crossing ?? scored.find((s) => tied.has(s.cause))!).cause;
  }
  const practice = scored.filter((s) => s.cause === cause).sort((a, b) => b.cost - a.cost || a.m.ply - b.m.ply)[0].m;
  return { cause, practice: { ply: practice.ply, san: practice.san }, costs };
}
